import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

/**
 * Premium product gallery:
 * - Main image with magnify-on-hover (transform-origin tracks mouse for true zoom)
 * - Thumbnails strip with active highlight
 * - Click main image → fullscreen lightbox with keyboard / arrow navigation
 */
export default function ProductGallery({ images = [], alt = "" }) {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState({ x: 50, y: 50, on: false });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => { setActive(0); }, [images]);

  const onMove = (e) => {
    const r = imgRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setZoom({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)), on: true });
  };
  const onLeave = () => setZoom((z) => ({ ...z, on: false }));

  const open = useCallback(() => setLightboxOpen(true), []);
  const close = useCallback(() => setLightboxOpen(false), []);
  const prev = useCallback(() => setActive((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setActive((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lightboxOpen, close, prev, next]);

  if (!images.length) {
    return (
      <div className="aspect-square rounded-2xl bg-larc-alt flex items-center justify-center text-larc-muted text-sm" data-testid="gallery-empty">
        Görsel yok
      </div>
    );
  }

  const src = images[active];

  return (
    <div data-testid="product-gallery">
      <div
        ref={imgRef}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={open}
        className="group relative aspect-square overflow-hidden rounded-2xl bg-larc-alt cursor-zoom-in select-none"
        data-testid="gallery-main"
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-200 ease-out"
          style={{
            transform: zoom.on ? "scale(1.8)" : "scale(1)",
            transformOrigin: `${zoom.x}% ${zoom.y}%`,
          }}
        />
        <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-larc-ink text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn size={12} /> Yakınlaştır
        </span>
      </div>

      {images.length > 1 && (
        <div className="mt-4 grid grid-cols-5 gap-3" data-testid="gallery-thumbs">
          {images.map((s, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`aspect-square overflow-hidden rounded-lg bg-larc-alt border-2 transition-all ${i === active ? "border-larc-gold" : "border-transparent hover:border-larc-border"}`}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Görsel ${i + 1}`}
            >
              <img src={s} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-fade-in"
          onClick={close}
          data-testid="lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 text-white inline-flex items-center justify-center hover:bg-white/20 transition-colors"
            data-testid="lightbox-close"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 sm:left-6 h-12 w-12 rounded-full bg-white/10 text-white inline-flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="lightbox-prev"
                aria-label="Önceki"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 sm:right-6 h-12 w-12 rounded-full bg-white/10 text-white inline-flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="lightbox-next"
                aria-label="Sonraki"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          <img
            onClick={(e) => e.stopPropagation()}
            src={src}
            alt={alt}
            className="max-h-[90vh] max-w-[92vw] object-contain rounded-xl shadow-2xl"
          />

          {images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.18em] uppercase">
              {active + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
